import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useOnboardingFormStore } from '@/lib/onboarding-form-store';
import { useAppForm } from '@/lib/use-app-form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@worldcoo/ui/components/card';
import { Button } from '@worldcoo/ui/components/button';
import { useState } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { step2Schema } from '@/lib/onboarding-schemas';
import { availableLanguages } from '@worldcoo/ui/components/form/available-languages';

export const Route = createFileRoute('/ngos/onboarding/contacts')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const { formData, setFormData, setCurrentStep } = useOnboardingFormStore();
	const [showContact2, setShowContact2] = useState(
		!!(formData.contact2Firstname || formData.contact2Lastname || formData.contact2Email),
	);

	const form = useAppForm({
		defaultValues: {
			contact1Firstname: formData.contact1Firstname,
			contact1Lastname: formData.contact1Lastname,
			contact1Position: formData.contact1Position,
			contact1Email: formData.contact1Email,
			contact1Phone: formData.contact1Phone,
			contact2Firstname: formData.contact2Firstname,
			contact2Lastname: formData.contact2Lastname,
			contact2Position: formData.contact2Position,
			contact2Email: formData.contact2Email,
			contact2Phone: formData.contact2Phone,
			contactLegalFirstname: formData.contactLegalFirstname,
			contactLegalLastname: formData.contactLegalLastname,
			contactLegalPosition: formData.contactLegalPosition,
			contactLegalId: formData.contactLegalId,
			language: formData.language,
			languages: formData.languages,
			defaultLanguage: formData.defaultLanguage,
		},
		validators: {
			onSubmit: step2Schema,
		},
		onSubmit: async ({ value }) => {
			setFormData(value);
			setCurrentStep(3);
			navigate({ to: '/ngos/onboarding/accreditation', search: (prev) => prev });
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const action = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('value');

		if (action === 'back') {
			setFormData(form.state.values);
			setCurrentStep(1);
			navigate({ to: '/ngos/onboarding', search: (prev) => prev });
			return;
		}

		form.handleSubmit();
	};

	return (
		<form id="onboarding-step-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
			{/* Contact Person #1 */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Contact Person #1`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<div className="flex gap-4">
						<form.AppField
							name="contact1Firstname"
							children={(field) => <field.TextField label={t`First Name`} className="w-full" />}
						/>
						<form.AppField
							name="contact1Lastname"
							children={(field) => <field.TextField label={t`Last Name`} className="w-full" />}
						/>
					</div>
					<form.AppField name="contact1Position" children={(field) => <field.TextField label={t`Position`} />} />
					<form.AppField
						name="contact1Phone"
						children={(field) => <field.TextField label={t`Phone`} placeholder="+34..." />}
					/>
					<form.AppField
						name="contact1Email"
						children={(field) => <field.TextField label={t`Email`} placeholder="email@example.org" />}
					/>
					<form.AppField
						name="language"
						children={(field) => (
							<field.SelectTextField
								label={t`Preferred Language`}
								options={availableLanguages.map((l) => ({ value: l.code, label: l.label }))}
								search={t`Search…`}
								empty={t`No results found.`}
							/>
						)}
					/>
				</CardContent>
			</Card>

			{/* Contact Person #2 (optional) */}
			<div>
				<div
					className="grid transition-[grid-template-rows,opacity] duration-300"
					style={{
						gridTemplateRows: showContact2 ? '1fr' : '0fr',
						opacity: showContact2 ? 1 : 0,
					}}
				>
					<div className="-m-1 overflow-hidden p-1">
						<Card className="shadow-warm">
							<CardHeader className="flex items-center justify-between">
								<CardTitle className="text-xl">{t`Contact Person #2`}</CardTitle>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-8"
									onClick={() => {
										setShowContact2(false);
										form.setFieldValue('contact2Firstname', '');
										form.setFieldValue('contact2Lastname', '');
										form.setFieldValue('contact2Position', '');
										form.setFieldValue('contact2Phone', '');
										form.setFieldValue('contact2Email', '');
									}}
								>
									<XIcon className="size-4" />
								</Button>
							</CardHeader>
							<CardContent className="flex flex-col gap-6">
								<div className="flex gap-4">
									<form.AppField
										name="contact2Firstname"
										children={(field) => <field.TextField label={t`First Name`} className="w-full" />}
									/>
									<form.AppField
										name="contact2Lastname"
										children={(field) => <field.TextField label={t`Last Name`} className="w-full" />}
									/>
								</div>
								<form.AppField name="contact2Position" children={(field) => <field.TextField label={t`Position`} />} />
								<form.AppField
									name="contact2Phone"
									children={(field) => <field.TextField label={t`Phone`} placeholder="+34..." />}
								/>
								<form.AppField
									name="contact2Email"
									children={(field) => <field.TextField label={t`Email`} placeholder="email@example.org" />}
								/>
							</CardContent>
						</Card>
					</div>
				</div>
				<div
					className="grid transition-[grid-template-rows,opacity] duration-300"
					style={{
						gridTemplateRows: !showContact2 ? '1fr' : '0fr',
						opacity: !showContact2 ? 1 : 0,
					}}
				>
					<div className="-m-1 overflow-hidden p-1">
						<Button type="button" variant="outline-dark" onClick={() => setShowContact2(true)} className="self-start">
							<PlusIcon className="mr-2 size-4" />
							{t`Add Contact Person`}
						</Button>
					</div>
				</div>
			</div>

			{/* Legal Representative */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Legal Representative`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<div className="flex gap-4">
						<form.AppField
							name="contactLegalFirstname"
							children={(field) => <field.TextField label={t`First Name`} className="w-full" />}
						/>
						<form.AppField
							name="contactLegalLastname"
							children={(field) => <field.TextField label={t`Last Name`} className="w-full" />}
						/>
					</div>
					<form.AppField name="contactLegalPosition" children={(field) => <field.TextField label={t`Position`} />} />
					<form.AppField
						name="contactLegalId"
						children={(field) => <field.TextField label={t`ID Document Number`} placeholder={t`12345678A`} />}
					/>
				</CardContent>
			</Card>

			{/* Languages */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Languages`}</CardTitle>
					<CardDescription className="leading-relaxed">
						{t`Select the languages your organization supports. Content will be translated to the selected languages.`}
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-2">
					<form.Subscribe selector={(state) => state.values.defaultLanguage}>
						{(defaultLanguage) => (
							<form.AppField name="languages">
								{(field) => (
									<field.LanguagesPickerField
										defaultText={t`Default`}
										setAsDefaultText={t`Set as default`}
										defaultLanguage={defaultLanguage}
										onDefaultLanguageChange={(lang) => form.setFieldValue('defaultLanguage', lang)}
									/>
								)}
							</form.AppField>
						)}
					</form.Subscribe>
				</CardContent>
			</Card>
		</form>
	);
}
